import React, { useState, useEffect } from 'react'
import { Plus, Edit2, Trash2, Loader2, Tag, X, Save, AlertCircle, ChevronLeft, ChevronRight, Check, MoreHorizontal } from 'lucide-react'
import { HRService } from '@/features/hr'

interface ListId {
  id: string
  name: string
  description: string | null
  color: string
  isActive: boolean
  isSystem: boolean
  createdAt: string
  updatedAt: string
  _count: {
    organizations: number
  }
}

interface ListIdManagerProps {
  onSuccess?: (message: string) => void
  onError?: (message: string) => void
}

export const ListIdManager: React.FC<ListIdManagerProps> = ({ onSuccess, onError }) => {
  const [listIds, setListIds] = useState<ListId[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newListIdName, setNewListIdName] = useState('')
  const [newListIdDescription, setNewListIdDescription] = useState('')
  const [newListIdColor, setNewListIdColor] = useState('#3B82F6')
  const [loadingAction, setLoadingAction] = useState(false)
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  
  // Inline editing state
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editColor, setEditColor] = useState('#3B82F6')

  const loadListIds = async () => {
    try {
      setLoading(true)
      const listIdsData = await (HRService as any).getAllListIds()
      setListIds(listIdsData)
    } catch (error) {
      console.error('Error loading list IDs:', error)
      if (onError) onError('Failed to load list IDs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadListIds()
  }, [])

  const handleCreateListId = async () => {
    if (!newListIdName.trim()) {
      if (onError) onError('List ID name is required')
      return
    }

    try {
      setLoadingAction(true)
      await (HRService as any).createListId({
        name: newListIdName.trim(),
        description: newListIdDescription.trim() || undefined,
        color: newListIdColor
      })
      
      setNewListIdName('')
      setNewListIdDescription('')
      setNewListIdColor('#3B82F6')
      setShowCreateModal(false)
      await loadListIds()
      
      if (onSuccess) onSuccess('List ID created successfully!')
    } catch (error) {
      console.error('Error creating list ID:', error)
      if (onError) onError('Failed to create list ID')
    } finally {
      setLoadingAction(false)
    }
  }

  const startEdit = (listId: ListId) => {
    setEditingId(listId.id)
    setEditName(listId.name)
    setEditDescription(listId.description || '')
    setEditColor(listId.color)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditName('')
    setEditDescription('')
    setEditColor('#3B82F6')
  }

  const saveEdit = async (listIdId: string) => {
    if (!editName.trim()) {
      if (onError) onError('List ID name is required')
      return
    }

    try {
      setLoadingAction(true)
      await (HRService as any).updateListId(listIdId, {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
        color: editColor
      })
      
      await loadListIds()
      cancelEdit()
      
      if (onSuccess) onSuccess('List ID updated successfully!')
    } catch (error) {
      console.error('Error updating list ID:', error)
      if (onError) onError('Failed to update list ID')
    } finally {
      setLoadingAction(false)
    }
  }

  const handleDeleteListId = async (listId: ListId) => {
    if (listId.isSystem) {
      if (onError) onError('Cannot delete system list IDs')
      return
    }

    const confirmMessage = listId._count.organizations > 0 
      ? `Are you sure you want to delete the list ID "${listId.name}"? This will unassign ${listId._count.organizations} organizations. This action cannot be undone.`
      : `Are you sure you want to delete the list ID "${listId.name}"? This action cannot be undone.`

    if (!window.confirm(confirmMessage)) {
      return
    }

    try {
      setLoadingAction(true)
      const response = await (HRService as any).deleteListId(listId.id)
      await loadListIds()
      
      if (onSuccess) onSuccess(response.message || 'List ID deleted successfully!')
    } catch (error) {
      console.error('Error deleting list ID:', error)
      if (onError) onError('Failed to delete list ID')
    } finally {
      setLoadingAction(false)
    }
  }

  const closeCreateModal = () => {
    setShowCreateModal(false)
    setNewListIdName('')
    setNewListIdDescription('')
    setNewListIdColor('#3B82F6')
  }

  // Pagination calculations
  const totalPages = Math.ceil(listIds.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentListIds = listIds.slice(startIndex, endIndex)

  // Color options with names
  const colorOptions = [
    { color: '#3B82F6', name: 'Blue' },
    { color: '#EF4444', name: 'Red' },
    { color: '#10B981', name: 'Green' },
    { color: '#F59E0B', name: 'Amber' },
    { color: '#8B5CF6', name: 'Purple' },
    { color: '#EC4899', name: 'Pink' },
    { color: '#06B6D4', name: 'Cyan' },
    { color: '#84CC16', name: 'Lime' }
  ]

  const getListIdColor = (color: string) => {
    return `border-2 border-${color.replace('#', '')} bg-${color.replace('#', '')} bg-opacity-10`
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-accent-900">List ID Management</h1>
            <p className="text-accent-600">Create, edit, and manage organization list IDs</p>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-accent-200 overflow-hidden">
          <div className="animate-pulse">
            <div className="border-b border-accent-200 p-4">
              <div className="h-6 bg-gray-200 rounded w-32"></div>
            </div>
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="border-b border-accent-200 p-4">
                <div className="flex items-center space-x-4">
                  <div className="w-4 h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                  <div className="h-4 bg-gray-200 rounded w-16"></div>
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                  <div className="flex space-x-2 ml-auto">
                    <div className="h-8 bg-gray-200 rounded w-16"></div>
                    <div className="h-8 bg-gray-200 rounded w-16"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-accent-900">List ID Management</h1>
          <p className="text-accent-600">Create, edit, and manage organization list IDs</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Create List ID</span>
        </button>
      </div>

      {/* List IDs Table */}
      <div className="bg-white rounded-lg border border-accent-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-accent-50 border-b border-accent-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <span>Color</span>
                    <div className="w-3 h-3 rounded-full border border-gray-300" style={{ backgroundColor: '#3B82F6' }} title="Color preview"></div>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                  Organizations
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-accent-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-accent-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-accent-200">
              {currentListIds.map((listId) => (
                <tr key={listId.id} className="hover:bg-accent-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-6 h-6 rounded-full border-2 shadow-sm"
                        style={{ backgroundColor: listId.color, borderColor: listId.color }}
                        title={`Color: ${listId.color}`}
                      ></div>
                      <div className="hidden sm:block">
                        <div className="text-xs text-accent-500 font-mono">
                          {listId.color}
                        </div>
                        <div className="text-xs text-accent-400">
                          {colorOptions.find(opt => opt.color === listId.color)?.name || 'Custom'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingId === listId.id ? (
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="w-full px-2 py-1 border border-accent-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                        placeholder="List ID name"
                      />
                    ) : (
                      <div className="text-sm font-medium text-accent-900">{listId.name}</div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {editingId === listId.id ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          className="w-full px-2 py-1 border border-accent-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                          placeholder="Description (optional)"
                        />
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-accent-500">Color:</span>
                          <div className="flex items-center space-x-1">
                            {colorOptions.map((option) => (
                              <button
                                key={option.color}
                                onClick={() => setEditColor(option.color)}
                                className={`w-6 h-6 rounded-full border-2 ${
                                  editColor === option.color ? 'border-gray-800 scale-110' : 'border-gray-300 hover:border-gray-500'
                                } transition-all duration-200`}
                                style={{ backgroundColor: option.color }}
                                title={`${option.name} (${option.color})`}
                              />
                            ))}
                          </div>
                          <input
                            type="color"
                            value={editColor}
                            onChange={(e) => setEditColor(e.target.value)}
                            className="w-8 h-6 border border-accent-300 rounded cursor-pointer"
                            title="Custom color"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-accent-600 max-w-xs truncate">
                        {listId.description || 'No description'}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-accent-600">
                      <Tag className="w-4 h-4 mr-1" />
                      {listId._count.organizations}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-accent-600">
                    {new Date(listId.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {editingId === listId.id ? (
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => saveEdit(listId.id)}
                          disabled={loadingAction || !editName.trim()}
                          className="text-green-600 hover:text-green-900 disabled:opacity-50"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={loadingAction}
                          className="text-accent-400 hover:text-accent-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => startEdit(listId)}
                          disabled={listId.isSystem}
                          className={`text-accent-600 hover:text-accent-900 ${
                            listId.isSystem ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          title={listId.isSystem ? 'System list IDs cannot be edited' : 'Edit list ID'}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteListId(listId)}
                          disabled={listId.isSystem}
                          className={`text-red-600 hover:text-red-900 ${
                            listId.isSystem ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          title={
                            listId.isSystem 
                              ? 'System list IDs cannot be deleted' 
                              : listId._count.organizations > 0 
                                ? `Delete list ID (will unassign ${listId._count.organizations} organizations)`
                                : 'Delete list ID'
                          }
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-accent-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-accent-300 text-sm font-medium rounded-md text-accent-700 bg-white hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-accent-300 text-sm font-medium rounded-md text-accent-700 bg-white hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-accent-700">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(endIndex, listIds.length)}</span> of{' '}
                  <span className="font-medium">{listIds.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-accent-300 bg-white text-sm font-medium text-accent-500 hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + 1
                    const isActive = pageNum === currentPage
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          isActive
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-accent-300 text-accent-500 hover:bg-accent-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-accent-300 bg-white text-sm font-medium text-accent-500 hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg transform transition-all duration-300 ease-out">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-accent-200 bg-gradient-to-r from-primary-50 to-accent-50">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <Tag className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-accent-900">Create New List ID</h2>
                  <p className="text-sm text-accent-600">Add a new organization list ID</p>
                </div>
              </div>
              <button
                onClick={closeCreateModal}
                className="p-2 text-accent-400 hover:text-accent-600 hover:bg-accent-100 rounded-lg transition-all duration-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Name Field */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-accent-700">
                  List ID Name *
                </label>
                <input
                  type="text"
                  value={newListIdName}
                  onChange={(e) => setNewListIdName(e.target.value)}
                  className="w-full px-4 py-3 border border-accent-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  placeholder="e.g., ELITE, PRIME, WIN, SUN"
                />
                <p className="text-xs text-accent-500">Choose a descriptive name for your list ID</p>
              </div>
              
              {/* Description Field */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-accent-700">
                  Description
                </label>
                <textarea
                  value={newListIdDescription}
                  onChange={(e) => setNewListIdDescription(e.target.value)}
                  className="w-full px-4 py-3 border border-accent-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-none"
                  placeholder="Optional description for this list ID..."
                  rows={3}
                />
                <p className="text-xs text-accent-500">Provide additional context about this list ID</p>
              </div>
              
              {/* Color Selection */}
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-accent-700">
                  Color Theme
                </label>
                
                {/* Quick Select Colors */}
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-accent-600 mb-2">Quick Select</p>
                    <div className="grid grid-cols-4 gap-2">
                      {colorOptions.map((option) => (
                        <button
                          key={option.color}
                          onClick={() => setNewListIdColor(option.color)}
                          className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                            newListIdColor === option.color 
                              ? 'border-primary-500 bg-primary-50 scale-105 shadow-md' 
                              : 'border-accent-200 hover:border-accent-300 hover:bg-accent-50'
                          }`}
                          title={`${option.name} (${option.color})`}
                        >
                          <div 
                            className="w-6 h-6 rounded-full mx-auto mb-1 shadow-sm"
                            style={{ backgroundColor: option.color }}
                          />
                          <p className="text-xs font-medium text-accent-700">{option.name}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Custom Color */}
                  <div className="border-t border-accent-200 pt-3">
                    <p className="text-sm text-accent-600 mb-2">Custom Color</p>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={newListIdColor}
                        onChange={(e) => setNewListIdColor(e.target.value)}
                        className="w-12 h-12 border-2 border-accent-300 rounded-lg cursor-pointer shadow-sm"
                        title="Custom color"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <div 
                            className="w-4 h-4 rounded-full border border-accent-300"
                            style={{ backgroundColor: newListIdColor }}
                          />
                          <span className="text-sm font-mono text-accent-600">{newListIdColor}</span>
                        </div>
                        <p className="text-xs text-accent-500 mt-1">Click to choose any color</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-accent-200 bg-accent-50">
              <button
                onClick={closeCreateModal}
                className="px-4 py-2 text-accent-600 hover:text-accent-800 hover:bg-accent-100 rounded-lg transition-all duration-200 font-medium"
                disabled={loadingAction}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateListId}
                disabled={loadingAction || !newListIdName.trim()}
                className="px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-accent-300 disabled:cursor-not-allowed text-white rounded-lg transition-all duration-200 font-medium flex items-center space-x-2 shadow-sm"
              >
                {loadingAction ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Create List ID</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}
